import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useScrollBottomAction } from '../../Hooks/useScrollBottomAction'
import { Typography, useMediaQuery } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import List, { ListOnDragProps } from '../List/List'
import Loading from '../Loading/Loading'
import { ErrorSkeleton } from '../Error/ErrorMessage'
import { useCustomTranslation } from '../../Hooks/useCustomTranslation'
import { useSelector } from 'react-redux'
import { DefaultRootState } from '../../State/RootReducer'
import { Payload } from '../../Hooks/AuthFetch/AuthFetch'
import { FetchProps, ListProps } from './ApiView'
import { InternalFilter } from './ApiViewModels'
import { useFetch, useFetchApiObject } from '../../Hooks/useFetch'
import { Paper } from '../Paper'
import { ListSkeleton } from '../Skeletons'
import { J1ToparrowD } from '@e3dc-react/icons'
import { FabButton } from '../Buttons'
import { Theme } from '@mui/material'

interface ApiListProps<T> {
  fetchProps: FetchProps
  listProps: ListProps<T>
  onSelect?: (item: T) => void
  customFunctionalities?: ((item: T) => React.ReactNode)[]
  noData?: string
  withLoading?: boolean
  draggable?: boolean
  filterString?: string
  oneFilterString?: string
  onDragEnd?: (props: ListOnDragProps) => Promise<void>
  internalFilters?: InternalFilter[]
  dragId?: (item: T) => number | string
  collapsibleContent?: (row: T) => React.ReactNode
  elevation?: number
  dataQaPrefix?: string
  onAfterFetch?: (data?: T[], count?: number) => void
  hasFloatingButton?: boolean
  scrollMarginTop?: number
  hasBottomBar?: boolean
  hideFab?: boolean
  customPayload?: Record<string, unknown>
  initialPageLimit?: number
}

interface StyleProps {
  scrollMarginTop?: number
  hasFloatingButton?: boolean
  hasBottomBar?: boolean
}

const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  typographyNoData: {
    marginLeft: theme.spacing(1),
  },
  scrollButton: {
    marginBottom: (props.hasBottomBar ? 56 : 0) + (props.hasFloatingButton ? 64 : 0),
  },
  paperScroll: {
    scrollMarginTop: props.scrollMarginTop ?? theme.spacing(8),
  },
}))

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ApiList = <T extends { [key: string]: any }>({
  fetchProps,
  listProps,
  onSelect,
  customFunctionalities,
  noData,
  onDragEnd,
  filterString,
  oneFilterString,
  internalFilters,
  dragId,
  collapsibleContent,
  dataQaPrefix,
  onAfterFetch,
  withLoading = true,
  draggable = false,
  elevation = 1,
  hasFloatingButton,
  scrollMarginTop,
  hasBottomBar,
  hideFab = false,
  customPayload,
  initialPageLimit = 10,
}: ApiListProps<T>): React.ReactElement => {
  const { t } = useCustomTranslation()
  const { url, endpoint, token, filter, orderBy, connect, searchPhrase } = fetchProps
  const ref = useRef<HTMLDivElement>(null)
  const firstPayload: Payload = { ...customPayload }
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'))

  if (filter) firstPayload.filter = filter
  if (filterString) firstPayload.filter = firstPayload.filter ? firstPayload.filter + ',' + filterString : filterString
  if (oneFilterString) firstPayload.filterOr = firstPayload.filterOr ? firstPayload.filterOr + ',' + oneFilterString : oneFilterString
  if (connect) firstPayload.connect = connect
  if (searchPhrase) firstPayload.searchPhrase = searchPhrase

  const { primary, secondary, icon, dataQa } = listProps
  const [offset, setOffset] = useState<number>(fetchProps.offset ?? 0)
  const refresh = useSelector<DefaultRootState>(state => state.apiView.refresh)
  const [payload, setPayload] = useState<Payload>(firstPayload)
  const [countPayload, setCountPayload] = useState<Payload>(firstPayload)
  const [internalFiltersActive, setInternalFiltersActive] = useState(false)
  const [accumulatedData, setAccumulatedData] = useState<T[]>([])
  const [limit] = useState<number>(fetchProps.limit ?? initialPageLimit)
  const { classes } = useStyles({ scrollMarginTop, hasFloatingButton, hasBottomBar })

  const [fetchCount] = useFetch<{ count: number }>({
    url: `${url}/stats`,
    payload: countPayload,
    token,
    endpoint,
    dependencies: [url, connect, filter, refresh, searchPhrase, countPayload, endpoint, token],
  })

  const [fetchedData, fetchedDataLoading, fetchedDataError] = useFetchApiObject<T[]>({
    url,
    payload,
    token,
    endpoint,
    dependencies: [limit, url, filter, connect, refresh, offset, searchPhrase, payload, token, endpoint],
  })

  useEffect(() => {
    onAfterFetch?.(fetchedData, fetchCount?.count)
  }, [fetchCount, fetchedData]) //eslint-disable-line

  // reset data
  useEffect(() => {
    setAccumulatedData([])
    setOffset(0)
    // filter can also be given to fetchProps instead of filtering via apiView
  }, [internalFiltersActive, filterString, oneFilterString, fetchProps.filter, searchPhrase])

  useEffect(() => {
    if (fetchedData && fetchCount && accumulatedData.length < fetchCount?.count && offset >= accumulatedData.length) {
      setAccumulatedData(data => [...data, ...fetchedData])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedData])

  useEffect(() => {
    setInternalFiltersActive(internalFilters?.find(filter => filter.value) ? true : false)
  }, [internalFilters])

  useEffect(() => {
    let isMounted = true

    const payload: Payload = {
      ...customPayload,
    }
    if (orderBy) {
      payload.sort = orderBy
    }
    if (limit !== undefined) {
      payload.limit = limit
    }
    if (offset !== undefined) {
      payload.offset = offset
    }
    if (filter) payload.filter = filter
    if (filterString) payload.filter = payload.filter ? payload.filter + ',' + filterString : filterString
    if (oneFilterString) payload.filterOr = payload.filterOr ? payload.filterOr + ',' + oneFilterString : oneFilterString
    if (connect) payload.connect = connect
    if (searchPhrase) payload.searchPhrase = searchPhrase
    if (isMounted) setPayload(payload)
    return () => {
      isMounted = false
    }
  }, [limit, url, filter, connect, orderBy, refresh, filterString, oneFilterString, internalFiltersActive, offset, searchPhrase, customPayload])

  useEffect((): (() => void) => {
    let isMounted = true

    const payload: Payload = { ...customPayload }

    if (filter) payload.filter = filter
    if (filterString) payload.filter = payload.filter ? payload.filter + ',' + filterString : filterString
    if (oneFilterString) payload.filterOr = payload.filterOr ? payload.filterOr + ',' + oneFilterString : oneFilterString
    if (connect) payload.connect = connect
    if (searchPhrase) payload.searchPhrase = searchPhrase

    if (isMounted) setCountPayload(payload)

    return () => (isMounted = false)
  }, [url, connect, filter, refresh, filterString, oneFilterString, internalFiltersActive, searchPhrase, customPayload])

  // updates limit of fetched data when scrolling on mobile view
  useScrollBottomAction(() => {
    if (fetchCount && accumulatedData.length < fetchCount?.count) {
      // inital data is fetched and offset is still = 0
      setOffset(offset => (offset === 0 ? limit : accumulatedData.length))
    }
  })

  const filteredData: T[] = internalFiltersActive
    ? accumulatedData?.filter(item =>
        internalFilters?.reduce<boolean>(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (prev, currFilter) => currFilter.filterFunction((item as any)[currFilter.name], currFilter.value) && prev,
          true
        )
      ) ?? []
    : accumulatedData ?? []

  const memoList = useMemo(
    () => (
      <List
        data={
          filteredData?.map(item => ({
            primary: primary?.(item),
            secondary: secondary?.(item),
            icon: icon?.(item),
            onSelect: () => onSelect?.(item),
            customFunctionalities: customFunctionalities?.map(functionality => functionality(item)),
            dragId: dragId?.(item),
            dataQa: dataQa?.(item),
          })) ?? []
        }
        draggable={draggable}
        onDragEnd={onDragEnd}
        loading={offset === 0 && fetchedDataLoading}
        dataQaPrefix={dataQaPrefix}
        collapsibleContent={collapsibleContent ? index => collapsibleContent(filteredData[index]) : undefined}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredData, offset, fetchedDataLoading, fetchedData]
  )
  if (offset === 0 && fetchedDataLoading) return <ListSkeleton />
  if (fetchedDataError) return <ErrorSkeleton title={t('errors.dataError')} />
  if (accumulatedData?.length === 0) return <Typography className={classes.typographyNoData}>{noData || t('shell.noData')}</Typography>

  return (
    <>
      <Paper elevation={elevation} paperRef={ref} className={classes.paperScroll}>
        {!hideFab && accumulatedData.length > 10 && isMobile && (
          <FabButton
            className={classes.scrollButton}
            onClick={() => ref?.current?.scrollIntoView({ block: 'start', behavior: 'smooth' })}
            icon={<J1ToparrowD />}
            fixed
            hasBottomBar={hasBottomBar}
          />
        )}
        {memoList}
      </Paper>
      {accumulatedData.length > 0 && withLoading && fetchedDataLoading && <Loading></Loading>}
    </>
  )
}

export default ApiList
